import React, { useMemo, useState, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, MOCK_PAYMENTS, Job, JobStatus } from '../../lib/supplierStore';
import { RoutePreviewMap } from '../../components/RoutePreviewMap';
import { Search, Calendar, MapPin, Clock, CheckCircle2, PlayCircle, User, UserCheck, Truck, CreditCard, Image as ImageIcon, History as HistoryIcon, ChevronLeft, Package, AlertTriangle, ZoomIn, X, Copy } from 'lucide-react';
type DetailTab = 'overview' | 'timeline' | 'photos' | 'payment';
function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function startOfDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}
function endOfDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}
function formatRangeLabel(start: string, end: string): string {
  const today = toISODate(new Date());
  if (start === end && start === today) return 'Today';
  if (start === end) {
    return new Date(start).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
  const s = new Date(start).toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  });
  const e = new Date(end).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  return `${s} – ${e}`;
}
const LIFECYCLE_STAGES: Array<{
  id: JobStatus;
  label: string;
}> = [{
  id: 'unassigned',
  label: 'Unassigned'
}, {
  id: 'pending_acceptance',
  label: 'Pending driver'
}, {
  id: 'accepted',
  label: 'Accepted'
}, {
  id: 'in_progress',
  label: 'In progress'
}, {
  id: 'at_pickup',
  label: 'At pickup'
}, {
  id: 'picked',
  label: 'Picked'
}, {
  id: 'at_dropoff',
  label: 'At dropoff'
}, {
  id: 'completed',
  label: 'Completed'
}];
function getStageIcon(stage: JobStatus) {
  switch (stage) {
    case 'completed':
      return CheckCircle2;
    case 'in_progress':
      return PlayCircle;
    case 'at_pickup':
    case 'at_dropoff':
      return MapPin;
    case 'picked':
      return Package;
    case 'accepted':
      return UserCheck;
    case 'pending_acceptance':
      return Clock;
    default:
      return AlertTriangle;
  }
}
function getStatusPill(status: JobStatus) {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
      };
    case 'unassigned':
      return {
        label: 'Unassigned',
        className: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
      };
    case 'pending_acceptance':
      return {
        label: 'Pending driver',
        className: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20'
      };
    case 'accepted':
      return {
        label: 'Accepted',
        className: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20'
      };
    default:
      return {
        label: 'In progress',
        className: 'bg-purple-50 text-purple-700 ring-1 ring-purple-600/20'
      };
  }
}
function formatTime(hour: number) {
  const h = Math.floor(hour);
  const m = (hour - h) * 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m === 0 ? '00' : m} ${ampm}`;
}
function formatRelative(ts?: number) {
  if (!ts) return null;
  const diff = Date.now() - ts;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
function formatAbsolute(ts?: number) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
function getJobSortTime(j: Job): number {
  return j.stageHistory?.completed || j.stageHistory?.at_dropoff || j.stageHistory?.picked || j.stageHistory?.at_pickup || j.stageHistory?.in_progress || j.stageHistory?.accepted || j.stageHistory?.pending_acceptance || 0;
}
export function SupplierHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobId = searchParams.get('jobId');
  const {
    jobs,
    drivers,
    trucks
  } = useSupplierStore();
  const [searchQuery, setSearchQuery] = useState('');
  const today = toISODate(new Date());
  const [rangeStart, setRangeStart] = useState<string>(today);
  const [rangeEnd, setRangeEnd] = useState<string>(today);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [lightbox, setLightbox] = useState<string | null>(null);
  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => getJobSortTime(b) - getJobSortTime(a));
  }, [jobs]);
  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    // Ensure start <= end (swap if user picked them in reverse).
    const fromIso = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
    const toIso = rangeStart <= rangeEnd ? rangeEnd : rangeStart;
    const fromMs = startOfDay(fromIso);
    const toMs = endOfDay(toIso);
    return sortedJobs.filter(j => {
      if (j.status !== 'completed') return false;
      const completedAt = j.stageHistory?.completed;
      if (!completedAt) return false;
      if (completedAt < fromMs || completedAt > toMs) return false;
      if (!q) return true;
      const driver = drivers.find(d => d.id === j.assignedDriverId);
      return j.id.toLowerCase().includes(q) || j.serviceType.toLowerCase().includes(q) || (j.customerName || '').toLowerCase().includes(q) || (j.customerPhone || '').toLowerCase().includes(q) || j.pickup.toLowerCase().includes(q) || j.dropoff.toLowerCase().includes(q) || (driver?.name || '').toLowerCase().includes(q);
    });
  }, [sortedJobs, rangeStart, rangeEnd, searchQuery, drivers]);
  const applyPreset = (preset: 'today' | '7d' | '30d' | 'year') => {
    const now = new Date();
    const end = toISODate(now);
    if (preset === 'today') {
      setRangeStart(end);
      setRangeEnd(end);
      return;
    }
    const start = new Date(now);
    if (preset === '7d') start.setDate(start.getDate() - 6);else if (preset === '30d') start.setDate(start.getDate() - 29);else if (preset === 'year') start.setMonth(0, 1);
    setRangeStart(toISODate(start));
    setRangeEnd(end);
  };
  const isPresetActive = (preset: 'today' | '7d' | '30d' | 'year'): boolean => {
    const now = new Date();
    const todayIso = toISODate(now);
    if (rangeEnd !== todayIso) return false;
    if (preset === 'today') return rangeStart === todayIso;
    const expected = new Date(now);
    if (preset === '7d') expected.setDate(expected.getDate() - 6);else if (preset === '30d') expected.setDate(expected.getDate() - 29);else if (preset === 'year') expected.setMonth(0, 1);
    return rangeStart === toISODate(expected);
  };
  const selectedJob = jobs.find(j => j.id === selectedJobId) || null;
  const assignedDriver = selectedJob?.assignedDriverId ? drivers.find(d => d.id === selectedJob.assignedDriverId) || null : null;
  const assignedTruck = selectedJob?.assignedTruckId ? trucks.find(t => t.id === selectedJob.assignedTruckId) || null : null;
  const payment = selectedJob ? MOCK_PAYMENTS.find(p => p.jobId === selectedJob.id) || null : null;
  // Deterministic completion photos for completed jobs (driver evidence)
  const completionPhotos = useMemo(() => {
    if (!selectedJob || selectedJob.status !== 'completed') return [];
    return [0, 1, 2].map(i => `https://picsum.photos/seed/${selectedJob.id}-${i}/600/400`);
  }, [selectedJob]);
  const handleSelectJob = (id: string) => {
    setSearchParams({
      jobId: id
    });
    setActiveTab('overview');
  };
  const handleCloseDetail = () => {
    setSearchParams({});
  };
  return <SupplierLayout data-id="element-3693">
      <div className="h-[calc(100vh-80px)] lg:h-[calc(100vh-64px)] flex flex-col" data-id="element-3694">
        <div className="flex-1 flex overflow-hidden" data-id="element-3695">
          {/* Left: Job list */}
          <div className={`w-full lg:w-[420px] lg:shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden ${selectedJob ? 'hidden lg:flex' : 'flex'}`} data-id="element-3696">
            <div className="bg-white border-b border-gray-200 shrink-0 p-4 space-y-3" data-id="element-3697">
              <h2 className="text-lg font-display font-bold text-gray-900 flex items-center gap-2" data-id="element-3698">
                <HistoryIcon className="w-5 h-5 text-brand-500" data-id="element-3699" />
                Job History
              </h2>
              {/* Search */}
              <div className="relative" data-id="element-3700">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3701" />
                <input type="text" placeholder="Search by id, customer, driver, address..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors" data-id="element-3702" />
              </div>
              {/* Date range presets */}
              <div className="flex flex-wrap gap-1.5" data-id="element-3703">
                {([{
                id: 'today',
                label: 'Today'
              }, {
                id: '7d',
                label: 'Last 7 days'
              }, {
                id: '30d',
                label: 'Last 30 days'
              }, {
                id: 'year',
                label: 'This year'
              }] as Array<{
                id: 'today' | '7d' | '30d' | 'year';
                label: string;
              }>).map(p => {
                const isActive = isPresetActive(p.id);
                return <button key={p.id} onClick={() => applyPreset(p.id)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${isActive ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3704">
                      {p.label}
                    </button>;
              })}
              </div>
              {/* Date inputs */}
              <div className="grid grid-cols-2 gap-2" data-id="element-3705">
                <label className="flex flex-col" data-id="element-3706">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3707">
                    From
                  </span>
                  <div className="relative" data-id="element-3708">
                    <Calendar className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3709" />
                    <input type="date" value={rangeStart} max={rangeEnd} onChange={e => setRangeStart(e.target.value)} className="w-full pl-8 pr-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3710" />
                  </div>
                </label>
                <label className="flex flex-col" data-id="element-3711">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3712">
                    To
                  </span>
                  <div className="relative" data-id="element-3713">
                    <Calendar className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3714" />
                    <input type="date" value={rangeEnd} min={rangeStart} onChange={e => setRangeEnd(e.target.value)} className="w-full pl-8 pr-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3715" />
                  </div>
                </label>
              </div>
              {/* Count summary */}
              <div className="flex items-center justify-between pt-1" data-id="element-3716">
                <p className="text-[11px] text-gray-500" data-id="element-3717">
                  {formatRangeLabel(rangeStart, rangeEnd)}
                </p>
                <p className="text-[11px] font-bold text-gray-900" data-id="element-3718">
                  {filteredJobs.length} completed{' '}
                  {filteredJobs.length === 1 ? 'job' : 'jobs'}
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2.5" data-id="element-3719">
              {filteredJobs.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-3720">
                  No jobs found matching your criteria.
                </div> : filteredJobs.map(job => {
              const pill = getStatusPill(job.status);
              const isSelected = selectedJobId === job.id;
              const driver = drivers.find(d => d.id === job.assignedDriverId);
              const truck = trucks.find(t => t.id === job.assignedTruckId);
              const sortTs = getJobSortTime(job);
              return <button key={job.id} onClick={() => handleSelectJob(job.id)} className={`w-full text-left p-4 rounded-2xl border transition-all ${isSelected ? 'bg-white border-brand-500 shadow-md ring-1 ring-brand-500' : 'bg-white border-gray-200 shadow-sm hover:border-brand-300'}`} data-id="element-3721">
                      <div className="flex justify-between items-start mb-2 gap-2" data-id="element-3722">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3723">
                          {job.id}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${pill.className}`} data-id="element-3724">
                          {pill.label}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm mb-1 truncate" data-id="element-3725">
                        {job.serviceType}
                      </h3>
                      {job.customerName && <div className="text-xs text-gray-700 font-medium flex items-center gap-1 mb-1 truncate" data-id="element-3726">
                          <User className="w-3 h-3 text-gray-400 shrink-0" data-id="element-3727" />
                          {job.customerName}
                        </div>}
                      {driver && <div className="text-xs text-gray-500 flex items-center gap-1 mb-1 truncate" data-id="element-3728">
                          <Truck className="w-3 h-3 text-gray-400 shrink-0" data-id="element-3729" />
                          {driver.name}
                          {truck && <span className="text-gray-400" data-id="element-3730">
                              · {truck.plate}
                            </span>}
                        </div>}
                      <div className="text-xs text-gray-500 truncate mb-2" data-id="element-3731">
                        <MapPin className="w-3 h-3 inline mr-1 text-gray-400" data-id="element-3732" />
                        {job.pickup}
                      </div>
                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100" data-id="element-3733">
                        <span className="text-sm font-bold text-gray-900" data-id="element-3734">
                          AED {job.price.toLocaleString()}
                        </span>
                        <span className="text-[11px] text-gray-400" data-id="element-3735">
                          {formatRelative(sortTs) || 'Unscheduled'}
                        </span>
                      </div>
                    </button>;
            })}
            </div>
          </div>

          {/* Right: Detail panel */}
          <div className={`flex-1 bg-white overflow-y-auto flex-col ${selectedJob ? 'flex' : 'hidden lg:flex'}`} data-id="element-3736">
            {!selectedJob ? <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8" data-id="element-3737">
                <HistoryIcon className="w-16 h-16 mb-4 text-gray-200" data-id="element-3738" />
                <p className="text-center max-w-xs" data-id="element-3739">
                  Select a job from the list to see its full history, timeline,
                  and evidence.
                </p>
              </div> : <DetailView job={selectedJob} driver={assignedDriver} truck={assignedTruck} payment={payment} completionPhotos={completionPhotos} activeTab={activeTab} onTabChange={setActiveTab} onClose={handleCloseDetail} onOpenPhoto={setLightbox} data-id="element-3740" />}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-3741">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-3742">
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label="Close" data-id="element-3743">
              <X className="w-5 h-5" data-id="element-3744" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2
        }} src={lightbox} alt="" onClick={e => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" data-id="element-3745" />
          </motion.div>}
      </AnimatePresence>
    </SupplierLayout>;
}
function DetailView({
  job,
  driver,
  truck,
  payment,
  completionPhotos,
  activeTab,
  onTabChange,
  onClose,
  onOpenPhoto
}: {
  job: Job;
  driver: ReturnType<typeof useSupplierStore>['drivers'][number] | null;
  truck: ReturnType<typeof useSupplierStore>['trucks'][number] | null;
  payment: (typeof MOCK_PAYMENTS)[number] | null;
  completionPhotos: string[];
  activeTab: DetailTab;
  onTabChange: (t: DetailTab) => void;
  onClose: () => void;
  onOpenPhoto: (src: string) => void;
}) {
  const pill = getStatusPill(job.status);
  const [copied, setCopied] = useState(false);
  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(job.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  const tabs: Array<{
    id: DetailTab;
    label: string;
  }> = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'timeline',
    label: 'Timeline'
  }, {
    id: 'photos',
    label: 'Photos'
  }, {
    id: 'payment',
    label: 'Payment'
  }];
  return <>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4" data-id="element-3746">
        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-3747">
          <div className="min-w-0 flex-1" data-id="element-3748">
            <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3749">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${pill.className}`} data-id="element-3750">
                {pill.label}
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3751">
                {job.id}
              </span>
              <button onClick={handleCopyId} className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 hover:text-gray-900 transition-colors px-1.5 py-0.5 rounded hover:bg-gray-100" data-id="element-3752">
                <Copy className="w-3 h-3" data-id="element-3753" />
                {copied ? 'Copied' : 'Copy id'}
              </button>
            </div>
            <h2 className="text-xl font-display font-bold text-gray-900 truncate" data-id="element-3754">
              {job.serviceType}
            </h2>
          </div>
          <button onClick={onClose} className="lg:hidden p-2 rounded-full hover:bg-gray-100 transition-colors shrink-0" aria-label="Back to list" data-id="element-3755">
            <ChevronLeft className="w-5 h-5 text-gray-600" data-id="element-3756" />
          </button>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 -mb-4" data-id="element-3757">
          {tabs.map(t => {
          const isActive = activeTab === t.id;
          return <button key={t.id} onClick={() => onTabChange(t.id)} className={`relative px-3 py-2.5 text-xs font-bold transition-colors ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3758">
                {t.label}
                {isActive && <motion.div layoutId="history-tab-underline" className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 rounded-full" transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-3759" />}
              </button>;
        })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 p-6" data-id="element-3760">
        <AnimatePresence mode="wait" data-id="element-3761">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15
        }} data-id="element-3762">
            {activeTab === 'overview' && <OverviewTab job={job} driver={driver} truck={truck} data-id="element-3763" />}
            {activeTab === 'timeline' && <TimelineTab job={job} driver={driver} truck={truck} data-id="element-3764" />}
            {activeTab === 'photos' && <PhotosTab job={job} driver={driver} completionPhotos={completionPhotos} onOpenPhoto={onOpenPhoto} data-id="element-3765" />}
            {activeTab === 'payment' && <PaymentTab job={job} payment={payment} data-id="element-3766" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </>;
}
function OverviewTab({
  job,
  driver,
  truck
}: {
  job: Job;
  driver: ReturnType<typeof useSupplierStore>['drivers'][number] | null;
  truck: ReturnType<typeof useSupplierStore>['trucks'][number] | null;
}) {
  return <div className="space-y-5" data-id="element-3767">
      {/* Route map */}
      <div className="rounded-2xl overflow-hidden border border-gray-200" data-id="element-3768">
        <RoutePreviewMap pickup={job.pickup} dropoff={job.dropoff} singleLocation={!job.dropoff || job.pickup === job.dropoff} eta={`${job.durationHours}h`} data-id="element-3769" />
      </div>

      {/* Customer */}
      {(job.customerName || job.customerPhone) && <section data-id="element-3770">
          <SectionLabel data-id="element-3771">Customer</SectionLabel>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3" data-id="element-3772">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0" data-id="element-3773">
              <User className="w-5 h-5 text-gray-500" data-id="element-3774" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-3775">
              <p className="font-bold text-gray-900 text-sm truncate" data-id="element-3776">
                {job.customerName || 'Customer'}
              </p>
              {job.customerPhone && <p className="text-xs text-gray-500" data-id="element-3777">{job.customerPhone}</p>}
            </div>
          </div>
        </section>}

      {/* Driver & truck */}
      <section data-id="element-3778">
        <SectionLabel data-id="element-3779">Driver & truck</SectionLabel>
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-id="element-3780">
          {driver ? <div className="flex items-center gap-3" data-id="element-3781">
              <div className="w-10 h-10 rounded-full bg-brand-500 text-white flex items-center justify-center font-bold shrink-0" data-id="element-3782">
                {driver.name.charAt(0)}
              </div>
              <div className="min-w-0 flex-1" data-id="element-3783">
                <p className="font-bold text-gray-900 text-sm truncate" data-id="element-3784">
                  {driver.name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3785">{driver.phone}</p>
              </div>
              {truck && <div className="text-right shrink-0" data-id="element-3786">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3787">
                    Truck
                  </p>
                  <p className="font-bold text-gray-900 text-sm" data-id="element-3788">
                    {truck.plate}
                  </p>
                  <p className="text-[11px] text-gray-500" data-id="element-3789">{truck.type}</p>
                </div>}
            </div> : <p className="text-sm text-gray-500" data-id="element-3790">No driver assigned.</p>}
        </div>
      </section>

      {/* Schedule + Route */}
      <section data-id="element-3791">
        <SectionLabel data-id="element-3792">Schedule & route</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3793">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-3" data-id="element-3794">
            <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0" data-id="element-3795">
              <Calendar className="w-4 h-4" data-id="element-3796" />
            </div>
            <div className="min-w-0" data-id="element-3797">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3798">
                Schedule
              </p>
              <p className="font-bold text-gray-900 text-sm" data-id="element-3799">
                {job.scheduledDate === 'today' ? 'Today' : 'Tomorrow'},{' '}
                {formatTime(job.startHour)}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3800">
                Duration: {job.durationHours}h
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-3" data-id="element-3801">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0" data-id="element-3802">
              <MapPin className="w-4 h-4" data-id="element-3803" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-3804">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3805">
                Route
              </p>
              <p className="font-bold text-gray-900 text-sm truncate" title={job.pickup} data-id="element-3806">
                {job.pickup}
              </p>
              <p className="text-xs text-gray-500 truncate" title={job.dropoff} data-id="element-3807">
                To: {job.dropoff}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-id="element-3808">
        <SectionLabel data-id="element-3809">Pricing</SectionLabel>
        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between" data-id="element-3810">
          <div data-id="element-3811">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3812">
              Job total
            </p>
            <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3813">
              AED {job.price.toLocaleString()}
            </p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center" data-id="element-3814">
            <CreditCard className="w-5 h-5" data-id="element-3815" />
          </div>
        </div>
      </section>
    </div>;
}
function TimelineTab({
  job,
  driver,
  truck
}: {
  job: Job;
  driver: ReturnType<typeof useSupplierStore>['drivers'][number] | null;
  truck: ReturnType<typeof useSupplierStore>['trucks'][number] | null;
}) {
  const stageEntries = LIFECYCLE_STAGES.map(s => ({
    ...s,
    ts: job.stageHistory?.[s.id]
  }));
  const currentIdx = LIFECYCLE_STAGES.findIndex(s => s.id === job.status);
  return <div className="space-y-6" data-id="element-3816">
      <section data-id="element-3817">
        <SectionLabel data-id="element-3818">Lifecycle</SectionLabel>
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-id="element-3819">
          <ol className="relative space-y-5 before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-px before:bg-gray-200" data-id="element-3820">
            {stageEntries.map((stage, i) => {
            const Icon = getStageIcon(stage.id);
            const isComplete = i <= currentIdx;
            const isCurrent = i === currentIdx;
            return <li key={stage.id} className="relative flex items-start gap-3 pl-0" data-id="element-3821">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${isComplete ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-400'} ${isCurrent ? 'ring-4 ring-brand-100' : ''}`} data-id="element-3822">
                    <Icon className="w-4 h-4" data-id="element-3823" />
                  </div>
                  <div className="min-w-0 flex-1 pt-1" data-id="element-3824">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap" data-id="element-3825">
                      <p className={`font-bold text-sm ${isComplete ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-3826">
                        {stage.label}
                      </p>
                      <span className="text-[11px] text-gray-500" data-id="element-3827">
                        {stage.ts ? formatRelative(stage.ts) : 'Pending'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-3828">
                      {stage.ts ? formatAbsolute(stage.ts) : '—'}
                    </p>
                  </div>
                </li>;
          })}
          </ol>
        </div>
      </section>

      <section data-id="element-3829">
        <SectionLabel data-id="element-3830">Assignment log</SectionLabel>
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-id="element-3831">
          {driver ? <div className="flex items-start gap-3" data-id="element-3832">
              <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0" data-id="element-3833">
                <UserCheck className="w-4 h-4" data-id="element-3834" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-3835">
                <p className="text-sm text-gray-900" data-id="element-3836">
                  Assigned to <span className="font-bold" data-id="element-3837">{driver.name}</span>
                  {truck && <span className="text-gray-500" data-id="element-3838">
                      {' '}
                      ({truck.plate} · {truck.type})
                    </span>}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-3839">
                  {job.stageHistory?.accepted ? formatAbsolute(job.stageHistory.accepted) : 'Time not recorded'}
                </p>
              </div>
            </div> : <p className="text-sm text-gray-500" data-id="element-3840">
              This job was never assigned to a driver.
            </p>}
        </div>
      </section>
    </div>;
}
function PhotosTab({
  job,
  driver,
  completionPhotos,
  onOpenPhoto
}: {
  job: Job;
  driver: ReturnType<typeof useSupplierStore>['drivers'][number] | null;
  completionPhotos: string[];
  onOpenPhoto: (src: string) => void;
}) {
  const customerPhotos = job.photos || [];
  const hasAny = customerPhotos.length > 0 || completionPhotos.length > 0;
  if (!hasAny) {
    return <div className="text-center py-12" data-id="element-3841">
        <ImageIcon className="w-12 h-12 mx-auto text-gray-200 mb-3" data-id="element-3842" />
        <p className="text-sm text-gray-500" data-id="element-3843">
          No photos available for this job.
        </p>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3844">
      {customerPhotos.length > 0 && <section data-id="element-3845">
          <SectionLabel data-id="element-3846">Customer photos</SectionLabel>
          <PhotoGrid photos={customerPhotos} onOpen={onOpenPhoto} data-id="element-3847" />
        </section>}
      {completionPhotos.length > 0 && <section data-id="element-3848">
          <div className="flex items-baseline justify-between mb-2" data-id="element-3849">
            <SectionLabel inline data-id="element-3850">Driver completion photos</SectionLabel>
            {driver && <p className="text-[11px] text-gray-500" data-id="element-3851">
                Uploaded by {driver.name}
              </p>}
          </div>
          <PhotoGrid photos={completionPhotos} onOpen={onOpenPhoto} data-id="element-3852" />
        </section>}
    </div>;
}
function PhotoGrid({
  photos,
  onOpen
}: {
  photos: string[];
  onOpen: (src: string) => void;
}) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-3853">
      {photos.map((src, i) => <button key={`${src}-${i}`} type="button" onClick={() => onOpen(src)} className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-brand-300 transition-all" aria-label={`View photo ${i + 1}`} data-id="element-3854">
          <img src={src} alt={`Photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-3855" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center" data-id="element-3856">
            <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3857" />
          </div>
        </button>)}
    </div>;
}
function PaymentTab({
  job,
  payment
}: {
  job: Job;
  payment: (typeof MOCK_PAYMENTS)[number] | null;
}) {
  const statusStyles: Record<string, string> = {
    Settled: 'bg-green-50 text-green-700 ring-1 ring-green-600/20',
    Pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
    Processing: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20'
  };
  if (!payment) {
    return <div className="space-y-5" data-id="element-3858">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-3" data-id="element-3859">
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center" data-id="element-3860">
            <CreditCard className="w-5 h-5" data-id="element-3861" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-3862">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3863">
              Job total
            </p>
            <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3864">
              AED {job.price.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-900" data-id="element-3865">
          No payment record found for this job yet.
        </div>
      </div>;
  }
  return <div className="space-y-4" data-id="element-3866">
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-id="element-3867">
        <div className="flex items-center justify-between mb-4" data-id="element-3868">
          <div data-id="element-3869">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3870">
              Amount
            </p>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-3871">
              AED {payment.amount.toLocaleString()}
            </p>
          </div>
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${statusStyles[payment.status] || 'bg-gray-100 text-gray-700'}`} data-id="element-3872">
            {payment.status}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm pt-4 border-t border-gray-100" data-id="element-3873">
          <div data-id="element-3874">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3875">
              Payment date
            </p>
            <p className="font-bold text-gray-900" data-id="element-3876">{payment.date}</p>
          </div>
          {payment.settlementDate && <div data-id="element-3877">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3878">
                Settled on
              </p>
              <p className="font-bold text-gray-900" data-id="element-3879">
                {payment.settlementDate}
              </p>
            </div>}
          <div data-id="element-3880">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3881">
              Customer
            </p>
            <p className="font-bold text-gray-900 truncate" data-id="element-3882">
              {payment.customer}
            </p>
          </div>
          <div data-id="element-3883">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-3884">
              Service
            </p>
            <p className="font-bold text-gray-900 truncate" data-id="element-3885">
              {payment.service}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
function SectionLabel({
  children,
  inline
}: {
  children: React.ReactNode;
  inline?: boolean;
}) {
  return <h3 className={`text-[10px] font-bold text-gray-500 uppercase tracking-wider ${inline ? '' : 'mb-2'}`} data-id="element-3886">
      {children}
    </h3>;
}